import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CButton, CCol, CFormInput, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";

import TNTable from "@/components/TNTable";
import CustomDropdown from "@/components/CustomDropdown";
import { useTranslationContext } from "@/context/TranslationContext";
import { serviceCategory } from "@/constants/translation/serviceCategory.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import { masters } from "@/libs/routes";
import constants from "@/libs/constant";
import { setFormatDate } from "@/utils/dateFormate";
import ReadMore from "@/components/ReadMore";
import {
  useListServiceCategory,
  useServiceCategoryDelete,
  useUpdateServiceCategoryStatus,
} from "@/hooks/useServiceCategory";

const STATUS_FILTER_VALUES = ["all", "active", "inactive"];

// API responses pass every value through the app's shared encryption round-trip,
// which stringifies booleans (true -> "true", false -> "false"). Native JS truthiness
// on the raw value is wrong here since the string "false" is truthy.
const isActiveValue = (value) => value === true || value === constants.status.apiStatus.true;

const ListServiceCategoryPage = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const canAdd = true;
  const canUpdate = true;
  const canDelete = true;

  const [currentPage, setCurrentPage] = useState(constants.pagination.defaultPage);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const searchTimeout = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setTempSearchText(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setSearchText(value);
      setCurrentPage(1);
    }, 400);
  };

  const handleClearFilter = () => {
    setTempSearchText("");
    setSearchText("");
    setStatusFilter("all");
    setCurrentPage(constants.pagination.defaultPage);
  };

  const {
    data: listData,
    isLoading,
    refetch,
  } = useListServiceCategory({
    page: currentPage || constants.pagination.defaultPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText && searchText.trim() ? { search: searchText.trim() } : {}),
    ...(statusFilter && statusFilter !== "all" ? { status: statusFilter } : {}),
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const [serviceCategoryList, setServiceCategoryList] = useState([]);
  const [paginationData, setPaginationData] = useState(null);

  useEffect(() => {
    if (listData && !isLoading) {
      setServiceCategoryList(listData.data?.serviceCategoryList || []);
      setPaginationData(listData.data?.pagination);
    }
  }, [listData, isLoading]);

  const { mutate: doDeleteServiceCategory } = useServiceCategoryDelete(
    (response) => {
      toast.success(response?.message || t(messages.toast.serviceCategory.deleted));
      refetch();
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.serviceCategory.deleteFailed));
    }
  );

  const { mutate: doUpdateStatus } = useUpdateServiceCategoryStatus(
    (response) => {
      toast.success(response?.message || t(messages.toast.serviceCategory.statusUpdated));
      refetch();
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.serviceCategory.statusUpdateFailed));
    }
  );

  const handleEditClick = (row) => {
    navigate(masters.serviceCategory.edit.replace(":id", row.uuid));
  };

  const handleDeleteClick = (row) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(serviceCategory.list.delete_popup_heading_text)}</h2>
            <p className="text-center mb-3">{t(serviceCategory.list.delete_popup_message_text)}</p>
            <div className="d-flex gap-2 justify-content-center">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doDeleteServiceCategory(row.uuid);
                  onClose();
                }}
              >
                {t(serviceCategory.list.delete_popup_yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(serviceCategory.list.delete_popup_no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleStatusClick = (row) => {
    const nextStatus = !isActiveValue(row.isActive);

    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(serviceCategory.list.status_popup_heading_text)}</h2>
            <div className="d-flex gap-2 justify-content-center">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doUpdateStatus({ uuid: row.uuid, isActive: nextStatus });
                  onClose();
                }}
              >
                {t(serviceCategory.list.status_popup_yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(serviceCategory.list.status_popup_no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const statusFilterOptions = useMemo(
    () =>
      STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: t(serviceCategory.list.status_filter_options[value]),
      })),
    [t]
  );

  const columns = useMemo(
    () => [
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t(serviceCategory.list.table.created_at_col_heading),
        cell: ({ getValue }) => <span>{setFormatDate(getValue())}</span>,
      },
      {
        id: "name",
        accessorKey: "name",
        header: t(serviceCategory.list.table.name_col_heading),
        cell: ({ row }) => <ReadMore maxLength={30}>{row.original.name}</ReadMore>,
      },
      {
        id: "isActive",
        accessorKey: "isActive",
        header: t(serviceCategory.list.table.status_col_heading),
        cell: ({ row }) => {
          const isActive = isActiveValue(row.original.isActive);
          return (
            <span
              className={`cursor-pointer ${isActive ? "text-success" : "text-danger"}`}
              onClick={() => canUpdate && handleStatusClick(row.original)}
            >
              {isActive ? t(serviceCategory.list.status.active) : t(serviceCategory.list.status.inactive)}
            </span>
          );
        },
      },
      {
        id: "actions",
        accessorKey: "uuid",
        header: t(serviceCategory.list.table.action_col_heading),
        cell: ({ row }) => (
          <div className="d-flex gap-2 justify-content-center">
            {canUpdate && (
              <div className="table-action-text" onClick={() => handleEditClick(row.original)}>
                <FontAwesomeIcon icon={faEdit} className="list-icon" />
              </div>
            )}
            {canDelete && (
              <div className="table-action-text" onClick={() => handleDeleteClick(row.original)}>
                <FontAwesomeIcon icon={faTrash} className="list-icon" />
              </div>
            )}
          </div>
        ),
      },
    ],
    [t]
  );

  return (
    <div className="service-category-list">
      <CRow className="list-align">
        <CCol lg={10} md={10} sm={12} xs={12} className="d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            placeholder={t(serviceCategory.list.search_placeholder)}
            type="text"
            value={tempSearchText}
            onChange={handleSearch}
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "300px", height: "38px" }}
          />

          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "200px", height: "38px", padding: "0px" }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={statusFilterOptions}
          />

          <CButton
            color="primary"
            type="button"
            className="filter-field-space"
            onClick={handleClearFilter}
            style={{ height: "36px",display: tempSearchText.trim() || statusFilter !== "all" ? "inline-block" : "none" }}
          >
            {t(serviceCategory.list.clear_filter_btn_text)}
          </CButton>

          {canAdd && (
            <CButton
              color="primary"
              type="button"
              className="filter-field-space"
              style={{ height: "38px"}}
              onClick={() => navigate(masters.serviceCategory.add)}
            >
              {t(serviceCategory.list.create_btn_text)}
            </CButton>
          )}
        </CCol>
      </CRow>

      <TNTable
        columns={columns}
        data={serviceCategoryList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
        t={t}
        responsive={false}
        className="service-category-table"
      />
    </div>
  );
};

ListServiceCategoryPage.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.serviceCategory?.list,
    pathname: masters.serviceCategory.list,
    active: true,
  },
];

export default ListServiceCategoryPage;
